// Vendor imports
const React = require('react')
// Client V2 common imports
const useClientEffect = require('client-v2/common/hooks/client-effect')
const { keyCodesByName } = require('client-v2/common/utils/events')

// Default option values
const DEFAULT_ROW_HEIGHT = 48 // px
const DEFAULT_VISIBLE_ROW_COUNT = 5

// Keycode constants
const DISMISS_KEYCODE = keyCodesByName.escape
const UP_KEYCODE = keyCodesByName.arrowUp
const DOWN_KEYCODE = keyCodesByName.arrowDown
const SELECT_KEYCODE = keyCodesByName.enter
const captureKeyCodes = [
  DISMISS_KEYCODE,
  UP_KEYCODE,
  DOWN_KEYCODE,
  SELECT_KEYCODE
]

/**
 * @param {Object} options
 * @param {number|null} options.activeIndex
 * @param {number} options.rowCount
 * @param {number} options.rowHeight
 * @param {React.RefObject<HTMLElement>} options.scrollableRef
 * @param {number} options.visibleRowCount
 * @param {() => void} options.onDismiss
 * @param {(index: number) => void} options.onIndexFocus
 * @param {(index: number) => void} options.onIndexSelect
 * @returns {void}
 */
function useKeyboardSelection({
  activeIndex,
  rowCount,
  rowHeight = DEFAULT_ROW_HEIGHT,
  scrollableRef,
  visibleRowCount = DEFAULT_VISIBLE_ROW_COUNT,
  onDismiss,
  onIndexFocus,
  onIndexSelect
}) {
  const [ scrollPosition, setScrollPosition ] = React.useState(0)

  const scrollToPosition = React.useCallback(
    newPosition => {
      const el = scrollableRef.current
      if (el) {
        setScrollPosition(newPosition)
        el.scrollTop = newPosition * rowHeight
      }
    },
    [ scrollableRef, rowHeight, setScrollPosition ]
  )

  const moveFocusUp = React.useCallback(
    () => {
      const newIndex = activeIndex === null ? -1 : activeIndex - 1
      // if index reaches the beginning of the list, scroll to the bottom
      if (newIndex < 0) {
        scrollToPosition(rowCount - visibleRowCount + 1)
        onIndexFocus(rowCount - 1)
        return
      }
      // if index is outside of the visible window, scroll one down
      if (newIndex < scrollPosition) {
        scrollToPosition(scrollPosition - 1)
      }
      onIndexFocus(newIndex)
    },
    [ activeIndex, onIndexFocus, rowCount, scrollPosition, scrollToPosition, visibleRowCount ]
  )

  const moveFocusDown = React.useCallback(
    () => {
      const newIndex = activeIndex === null ? 0 : activeIndex + 1
      // if index reaches the end of the list, scroll to the top
      if (newIndex >= rowCount) {
        scrollToPosition(0)
        onIndexFocus(0)
        return
      }
      // if index is outside of the visible window, scroll one up
      if (newIndex > scrollPosition + visibleRowCount - 1) {
        scrollToPosition(scrollPosition + 1)
      }
      onIndexFocus(newIndex)
    },
    [ activeIndex, onIndexFocus, rowCount, scrollPosition, scrollToPosition, visibleRowCount ]
  )

  const onKeyDown = React.useCallback(
    event => {
      const { keyCode } = event
      if (!rowCount || !captureKeyCodes.includes(keyCode)) {
        return
      }
      event.preventDefault()
      switch (keyCode) {
        case DISMISS_KEYCODE:
          onIndexFocus(null)
          onDismiss()
          break
        case SELECT_KEYCODE:
          onIndexSelect(activeIndex)
          break
        case UP_KEYCODE:
          moveFocusUp()
          break
        case DOWN_KEYCODE:
          moveFocusDown()
          break
      }
    },
    [ activeIndex, moveFocusDown, moveFocusUp, onDismiss, onIndexFocus, onIndexSelect, rowCount ]
  )

  useClientEffect(
    () => {
      window.addEventListener('keydown', onKeyDown)
      return () => {
        window.removeEventListener('keydown', onKeyDown)
      }
    },
    [ onKeyDown ]
  )
}

module.exports = useKeyboardSelection
